import { useState, useEffect, useMemo, useRef } from 'react'
import { FiShield, FiCalendar, FiCamera, FiUser, FiMail, FiPhone, FiAtSign, FiCheck } from 'react-icons/fi'
import api from '../../utils/api'
/* Hooks */
import useFlashMessage from '../../hooks/useFlashMessage'
import AuthImage from '../../shared/components/AuthImage/AuthImage'
import './Perfil.css'

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

function getInitials(name) {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/)
    if (parts.length === 1) return parts[0][0].toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function getRoleLabel(user) {
    if (user.technician) return 'Técnico'
    const group = user.permission?.group
    if (group === 'DEV_MASTER') return 'Administrador Master'
    if (group === 'ADM_USER') return 'Administrador'
    return 'Usuário'
}

function getMemberSince(createdAt) {
    if (!createdAt) return null
    const date = new Date(createdAt)
    return `${MONTHS[date.getMonth()]}/${date.getFullYear()}`
}

function Perfil({ btCancelAction }) {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()
    const fileInputRef = useRef(null)

    const [user, setUser] = useState({})
    const [imageFile, setImageFile] = useState(null)
    const [load, setLoad] = useState(false)

    useEffect(() => {
        api.get('/user/perfil/me', {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => {
            setUser(response.data.user)
            setLoad(true)
        })
    }, [token])

    function handleChange(e) {
        setUser((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    }

    function onFileChange(e) {
        const file = e.target.files[0]
        if (!file) return
        setImageFile(file)
    }

    async function submit(e) {
        e.preventDefault()
        let msgType = 'success'

        const formData = new FormData()
        formData.append('name', user.name || '')
        formData.append('email', user.email || '')
        formData.append('phone', user.phone || '')
        formData.append('birth', user.birth || '')
        formData.append('userName', user.userName || '')
        if (imageFile) formData.append('image', imageFile)

        const data = await api.patch('/user/perfil', formData, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data)
            .catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') setImageFile(null)
    }

    const initials = useMemo(() => getInitials(user.name), [user.name])
    const roleLabel = useMemo(() => getRoleLabel(user), [user])
    const memberSince = useMemo(() => getMemberSince(user.createdAt), [user.createdAt])

    if (!load) {
        return <div className="pg-loading">Carregando perfil...</div>
    }

    return (
        <form onSubmit={submit} className="perfil-page">
            <div className="perfil-header">
                <div className="perfil-avatar-wrap" onClick={() => fileInputRef.current.click()} title="Alterar foto">
                    {imageFile ? (
                        <div className="perfil-avatar-photo-wrap">
                            <img className="perfil-avatar-photo" src={URL.createObjectURL(imageFile)} alt="" />
                        </div>
                    ) : user.image ? (
                        <div className="perfil-avatar-photo-wrap">
                            <AuthImage className="perfil-avatar-photo" src={`/images/user/${user.image}`} alt="" />
                        </div>
                    ) : (
                        <div className="perfil-avatar">{initials}</div>
                    )}
                    <span className="perfil-avatar-edit"><FiCamera size={13} /></span>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg"
                        className="perfil-avatar-input"
                        onChange={onFileChange}
                    />
                </div>
                <div className="perfil-header-info">
                    <h2 className="perfil-name">{user.name || 'Meu Perfil'}</h2>
                    <p className="perfil-email">{user.email}</p>
                    <div className="perfil-chips">
                        <span className="perfil-chip"><FiShield size={12} />{roleLabel}</span>
                        {memberSince && <span className="perfil-chip"><FiCalendar size={12} />Desde {memberSince}</span>}
                    </div>
                </div>
            </div>

            <div className="perfil-fields-grid">
                <div className="perfil-field perfil-field-full">
                    <label><FiUser size={13} />Nome completo</label>
                    <input type="text" name="name" placeholder="Digite o seu nome" onChange={handleChange} value={user.name || ''} />
                </div>

                <div className="perfil-field">
                    <label><FiMail size={13} />E-mail</label>
                    <input type="email" name="email" placeholder="Digite o e-mail" onChange={handleChange} value={user.email || ''} />
                </div>
                <div className="perfil-field">
                    <label><FiPhone size={13} />Telefone WhatsApp</label>
                    <input type="tel" name="phone" placeholder="Digite o telefone" onChange={handleChange} value={user.phone || ''} />
                </div>

                <div className="perfil-field">
                    <label><FiCalendar size={13} />Data de nascimento</label>
                    <input type="date" name="birth" onChange={handleChange} value={user.birth || ''} />
                </div>
                <div className="perfil-field">
                    <label><FiAtSign size={13} />Usuário</label>
                    <input type="text" name="userName" value={user.userName || ''} disabled />
                </div>
            </div>

            <div className="perfil-actions">
                {btCancelAction && (
                    <button type="button" className="perfil-btn-ghost" onClick={btCancelAction}>Cancelar</button>
                )}
                <button type="submit" className="perfil-btn-primary"><FiCheck size={15} />Salvar alterações</button>
            </div>
        </form>
    )
}

export default Perfil
